import React, { useContext, useEffect, useState } from "react";
import { Card, Tag, Tabs, Spin, Empty, Typography, Badge } from "antd";
import { AndroidOutlined, AppleOutlined, FieldTimeOutlined, ThunderboltOutlined, LinkOutlined } from "@ant-design/icons";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";

const { Text, Title } = Typography;

const labelStyle = {
    fontSize: 12,
    color: "var(--color-text-secondary, #888)",
    marginBottom: 2,
    display: "block",
};

const valueStyle = {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--color-text-primary, #222)",
    wordBreak: "break-all",
};

function InfoRow({ label, value, isLink }) {
    return (
        <div style={{ marginBottom: 14 }}>
            <span style={labelStyle}>{label}</span>
            {isLink ? (
                <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 14, wordBreak: "break-all" }}>
                    <LinkOutlined style={{ marginRight: 4, fontSize: 12 }} />
                    {value}
                </a>
            ) : (
                <span style={valueStyle}>{value ?? "—"}</span>
            )}
        </div>
    );
}

function VersionCard({ data, platform }) {
    if (!data) return <Empty description="No data available" style={{ padding: "32px 0" }} />;

    const isAndroid = platform === "android";

    const updateTypeColor = data.update_type === "immediate" ? "error" : "processing";
    const updateTypeLabel = data.update_type === "immediate" ? "Immediate (Force)" : "Flexible";

    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>

            {/* Version info */}
            <div style={{
                background: "var(--color-background-primary, #fff)",
                border: "0.5px solid var(--color-border-tertiary, #e0e0e0)",
                borderRadius: 12,
                padding: "16px 20px",
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    {isAndroid
                        ? <AndroidOutlined style={{ fontSize: 16, color: "#3ddc84" }} />
                        : <AppleOutlined style={{ fontSize: 16, color: "#555" }} />}
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary, #666)" }}>
                        Version Info
                    </span>
                </div>

                <InfoRow label="Latest Version" value={data.latest_version} />
                <InfoRow label="Force Update Version" value={data.force_update_version} />

                <div style={{ marginBottom: 14 }}>
                    <span style={labelStyle}>Update Type</span>
                    <Badge status={updateTypeColor} text={
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{updateTypeLabel}</span>
                    } />
                </div>
            </div>

            {/* Store & remarks */}
            <div style={{
                background: "var(--color-background-primary, #fff)",
                border: "0.5px solid var(--color-border-tertiary, #e0e0e0)",
                borderRadius: 12,
                padding: "16px 20px",
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <LinkOutlined style={{ fontSize: 14, color: "var(--color-text-secondary, #888)" }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary, #666)" }}>
                        Store & Notes
                    </span>
                </div>

                <InfoRow label="Store URL" value={data.store_url} isLink={!!data.store_url} />
                <InfoRow label="Remarks" value={data.version_remarks || data.remarks} />
            </div>
        </div>
    );
}

export default function ViewAppVersions() {
    const { authData, backUrl } = useContext(AuthContext);
    const [androidData, setAndroidData] = useState(null);
    const [iosData, setIosData] = useState(null);
    const [loading, setLoading] = useState(true);

    const authHeader = { Authorization: `Bearer ${authData?.access}` };

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [androidRes, iosRes] = await Promise.all([
                    axios.get(`${backUrl}/api/emp/android/update-status/?platform=android`, { headers: authHeader }),
                    axios.get(`${backUrl}/api/emp/ios/update-status/?platform=ios`, { headers: authHeader }),
                ]);
                setAndroidData(androidRes.data);
                setIosData(iosRes.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    const tabItems = [
        {
            key: "android",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AndroidOutlined style={{ fontSize: 14, color: "#3ddc84" }} />
                    Android
                    {androidData?.latest_version && (
                        <Tag color="success" style={{ marginLeft: 4, fontSize: 11 }}>
                            v{androidData.latest_version}
                        </Tag>
                    )}
                </span>
            ),
            children: <VersionCard data={androidData} platform="android" />,
        },
        {
            key: "ios",
            label: (
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <AppleOutlined style={{ fontSize: 14 }} />
                    iOS
                    {iosData?.latest_version && (
                        <Tag color="default" style={{ marginLeft: 4, fontSize: 11 }}>
                            v{iosData.latest_version}
                        </Tag>
                    )}
                </span>
            ),
            children: <VersionCard data={iosData} platform="ios" />,
        },
    ];

    return (
        <div style={{ maxWidth: 720, margin: "0 auto", padding: 24 }}>
            <Card
                title={
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        App Version Status
                    </span>
                }
            >
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Tabs defaultActiveKey="android" items={tabItems} />
                )}
            </Card>
        </div>
    );
}